import { Modal } from 'theme';

/**
 * <geolocation-popup>
 *
 * Detects the visitor's optimal market using Shopify's native localization
 * discovery endpoint (browsing_context_suggestions.json) and, when the visitor
 * is browsing a market that does not match their location, offers to switch
 * instead of redirecting automatically.
 *
 * The copy is data-driven: one "market" block per target market holds the
 * heading/subheading/button/link/info text already written in that market's
 * main language. On a mismatch, the matching block is picked and injected.
 *
 * Switching is done through the native {% form 'localization' %} (Shopify
 * redirects to the correct market/domain with the chosen language active).
 */ 
(function () {
  'use strict';

  const STORAGE_KEY = 'theme:geolocation-decided';

  // Extends the theme's Modal so it inherits the native overlay, close button,
  // focus-lock, animations and shadow-DOM structure (matches the theme's other modals).
  class GeolocationPopup extends Modal {
    connectedCallback() {
      super.connectedCallback();

      this._markets = this._readConfig();
      this._headingEl = this.querySelector('[data-geo-heading]');
      this._subheadingEl = this.querySelector('[data-geo-subheading]');
      this._buttonEl = this.querySelector('[data-geo-button]');
      this._linkEl = this.querySelector('[data-geo-link]');
      this._infoEl = this.querySelector('[data-geo-info]');
      this._countryInput = this.querySelector('[data-geo-country-input]');
      this._localeInput = this.querySelector('[data-geo-locale-input]');
      this._form = this.querySelector('#geolocation-form');

      // The "stay" link dismisses (= stay in current market). The overlay and
      // close button are handled by the Modal base class.
      this.querySelector('[data-geo-stay]')?.addEventListener('click', () => this.hide(), {
        signal: this.abortController.signal,
      });

      // Persist the decision on any dismissal so the popup never returns.
      this.addEventListener('dialog:after-hide', () => this._remember(), {
        signal: this.abortController.signal,
      });

      // On switch: persist the decision, then either redirect to the market's
      // domain (when a Target URL is set) or let the native localization form
      // submit (Shopify handles the market switch).
      this._form?.addEventListener('submit', (event) => {
        this._remember();

        if (this._targetUrl) {
          event.preventDefault();
          window.location.assign(this._targetUrl);
        }
      }, {
        signal: this.abortController.signal,
      });

      // The Modal base class moves this element to <body> when it opens, which
      // re-fires connectedCallback. Guard so auto-show only ever runs once —
      // otherwise a stray timer re-opens the popup after it's been dismissed.
      if (!window.Shopify?.designMode && !this._autoShowRan) {
        this._autoShowRan = true;
        this._maybeShow();
      }
    }

    get apparitionDelay() {
      return (parseFloat(this.getAttribute('apparition-delay')) || 0) * 1000;
    }

    _remember() {
      try {
        localStorage.setItem(STORAGE_KEY, 'true');
      } catch (error) {
        /* localStorage unavailable — ignore */
      }
    }

    _readConfig() {
      const node = this.querySelector('[data-geo-config]');

      if (!node) {
        return [];
      }

      try {
        return JSON.parse(node.textContent) || [];
      } catch (error) {
        return [];
      }
    }

    /**
     * Return the market block whose country list contains the given ISO code,
     * falling back to the block flagged as the international / catch-all market.
     */
    _matchMarket(countryIso) {
      const iso = (countryIso || '').toUpperCase();

      const matched = this._markets.find((market) =>
        (market.countries || '')
          .toUpperCase()
          .split(/[\s,]+/)
          .filter(Boolean)
          .includes(iso)
      );

      return matched || this._markets.find((market) => market.international) || null;
    }

    async _maybeShow() {
      if (!this._markets.length) {
        return;
      }

      try {
        if (localStorage.getItem(STORAGE_KEY) === 'true') {
          return;
        }
      } catch (error) {
        /* localStorage unavailable — continue */
      }

      const detection = await this._fetchDetection();

      if (!detection) {
        return;
      }

      const detectedMarket = this._matchMarket(detection.countryIso);
      const currentMarket = this._matchMarket(window.Shopify?.country || '');

      // Only show when the visitor's detected country belongs to a different
      // market than the one they're currently browsing.
      if (!detectedMarket || detectedMarket === currentMarket) {
        return;
      }

      this._populate(detectedMarket, detection);

      window.setTimeout(() => {
        if (!this.open) {
          this.show();
        }
      }, this.apparitionDelay);
    }

    /**
     * Query Shopify's localization discovery endpoint. We read `detected_values`
     * (the raw geo-IP detected country, always returned) rather than relying on
     * `suggestions` (only populated when Shopify has a distinct better market —
     * which can be empty, e.g. for the US). We then decide the mismatch ourselves.
     */
    async _fetchDetection() {
      const root = (window.Shopify?.routes?.root) || '/';
      const currentCountry = window.Shopify?.country || '';
      const currentLanguage = window.Shopify?.language || '';

      const url =
        root +
        'browsing_context_suggestions.json' +
        '?country[enabled]=true' +
        '&country[exclude]=' + encodeURIComponent(currentCountry) +
        '&language[enabled]=true' +
        '&language[exclude]=' + encodeURIComponent(currentLanguage);

      try {
        const response = await fetch(url);

        if (!response.ok) {
          return null;
        }

        const data = await response.json();
        const detectedCountry = data?.detected_values?.country;
        const suggestedParts = data?.suggestions?.[0]?.parts;
        const countryIso = detectedCountry?.handle || suggestedParts?.country?.handle;

        if (!countryIso) {
          return null;
        }

        return {
          countryIso: countryIso,
          // Fallback name only — _populate re-localizes it to the target language.
          countryName: suggestedParts?.country?.name || detectedCountry?.name || countryIso,
          localeIso: suggestedParts?.language?.handle || '',
        };
      } catch (error) {
        return null;
      }
    }

    /**
     * The detected country name from Shopify comes back in the CURRENT storefront
     * language (e.g. "Frankreich" on the German store). We want it in the TARGET
     * market's language, so re-derive it with Intl.DisplayNames using the block's
     * locale, falling back to Shopify's value if that's not possible.
     */
    _countryNameInTargetLanguage(market, suggestion) {
      const locale = market.locale || suggestion.localeIso;

      if (locale && suggestion.countryIso) {
        try {
          const localized = new Intl.DisplayNames([locale], { type: 'region' }).of(suggestion.countryIso);
          if (localized && localized !== suggestion.countryIso) {
            return localized;
          }
        } catch (error) {
          /* Intl unsupported / bad locale — fall through */
        }
      }

      return suggestion.countryName;
    }

    _populate(market, suggestion) {
      // When set, the switch button redirects straight to this market's domain
      // (used for markets that are separate stores/domains, e.g. the COM shop).
      this._targetUrl = market.url || '';

      if (this._headingEl && market.heading) {
        const countryName = this._countryNameInTargetLanguage(market, suggestion);
        this._headingEl.textContent = market.heading.replace('{country}', countryName);
      }

      if (this._subheadingEl) {
        this._subheadingEl.textContent = market.subheading || '';
        this._subheadingEl.hidden = !market.subheading;
      }

      if (this._buttonEl && market.button) {
        this._buttonEl.textContent = market.button;
      }

      if (this._linkEl && market.link) {
        this._linkEl.textContent = market.link;
      }

      if (this._infoEl) {
        this._infoEl.textContent = market.info || '';
        this._infoEl.hidden = !market.info;
      }

      // Feed the native localization form so Shopify redirects to the right market.
      if (this._countryInput) {
        this._countryInput.value = suggestion.countryIso;
      }

      if (this._localeInput) {
        this._localeInput.value = market.locale || suggestion.localeIso || this._localeInput.value;
      }
    }
  }

  if (!window.customElements.get('geolocation-popup')) {
    window.customElements.define('geolocation-popup', GeolocationPopup);
  }
})();
